import { useState, useEffect } from "react";
import { ref, onValue, off, update } from "firebase/database";
import { database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import useMoveTo from "../constants/navigation";
import { ROUTES } from "../App";
import { ICONS } from "../constants/icons";
import { BsBriefcase, BsPlusLg } from "react-icons/bs";
import { CiSearch } from "react-icons/ci";
import { HiOutlineAdjustmentsHorizontal } from "react-icons/hi2";

const MyJobPosts = () => {
    const { currentUser } = useUser();
    const navigate = useMoveTo();
    const [jobs, setJobs] = useState([]);
    const [applicantCounts, setApplicantCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("active");
    const [searchQuery, setSearchQuery] = useState("");

    // Load this user's jobs
    useEffect(() => {
        const jobsRef = ref(database, "Jobs");
        onValue(jobsRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const myJobs = Object.entries(data)
                    .map(([id, job]) => ({ id, ...job }))
                    .filter((job) => job.hostId === currentUser.uid)
                    .sort((a, b) => b.createdAt - a.createdAt);
                setJobs(myJobs);
            } else {
                setJobs([]);
            }
            setLoading(false);
        }, (error) => {
            console.error("MyJobPosts fetch error:", error);
            setLoading(false);
        });
        return () => off(jobsRef);
    }, [currentUser.uid]);

    // Load applicant counts for each job
    useEffect(() => {
        if (!jobs.length) return;

        const cleanups = jobs.map((job) => {
            const appRef = ref(database, `JobApplications/${job.id}`);
            onValue(appRef, (snap) => {
                const count = snap.exists() ? Object.keys(snap.val()).length : 0;
                setApplicantCounts((prev) => ({ ...prev, [job.id]: count }));
            });
            return () => off(appRef);
        });

        return () => cleanups.forEach((fn) => fn());
    }, [jobs]);

    const handleToggleStatus = async (job) => {
        const newStatus = job.status === "active" ? "closed" : "active";
        try {
            await update(ref(database, `Jobs/${job.id}`), { status: newStatus });
        } catch (e) {
            console.error("Toggle status error:", e);
            window.showToast("Failed to update. Try again.", "error");
        }
    };

    const activeJobs = jobs.filter((j) => j.status === "active");
    const closedJobs = jobs.filter((j) => j.status === "closed");
    const totalApplicants = Object.values(applicantCounts).reduce(
        (sum, n) => sum + n,
        0
    );
    const displayedJobs = activeTab === "active" ? activeJobs : closedJobs;
    const searchedJobs = displayedJobs.filter((job) => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return (
            job.title?.toLowerCase().includes(q) ||
            job.role?.toLowerCase().includes(q) ||
            job.location?.toLowerCase().includes(q) ||
            job.payRate?.toLowerCase().includes(q) ||
            job.companyName?.toLowerCase().includes(q)
        );
    });

    return (
        <div className="flex flex-col h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-hidden">
            <MobileHeader
                title="My Job Posts"
                isBackBtn
                rightElement={
                    <button
                        onClick={() => navigate(ROUTES.JOBS_POST)}
                        className="p-2 rounded-lg bg-[#327691] text-white"
                        aria-label="Post a job"
                    >
                        <BsPlusLg className="w-4 h-4" />
                    </button>
                }
            />
            <div className="flex flex-1 overflow-hidden">
                <Sidebar />
                <main className="flex-1 overflow-y-auto pb-24 md:pb-5 px-4 pt-5 bg-white dark:bg-[#1A1A1A]">
                    <div className="flex items-center justify-between mb-4">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <img src={ICONS.arrowBack} className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert" alt="Back" />
                        </button>
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">
                            My Job Posts
                        </h2>
                        <div className="w-5" />
                    </div>

                    <div className="hidden md:flex justify-end mb-6">
                        <button
                            onClick={() => navigate(ROUTES.JOBS_POST)}
                            className="items-center gap-2 px-4 py-2 bg-[#327691] text-white rounded-xl text-sm font-semibold flex"
                        >
                            <BsPlusLg className="w-4 h-4" />
                            Post a Job
                        </button>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-3 mb-6">
                        <StatCard
                            label="Active"
                            value={activeJobs.length}
                            color="text-green-500"
                        />
                        <StatCard
                            label="Closed"
                            value={closedJobs.length}
                            color="text-gray-400"
                        />
                        <StatCard
                            label="Applicants"
                            value={totalApplicants}
                            color="text-[#327691]"
                        />
                    </div>

                    {/* Controls: tabs + search */}
                    <div className="w-full flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 mb-4 border-b border-gray-200 dark:border-[#D9D9D9]/20 pb-4">
                        <div className="flex flex-wrap gap-2 w-full lg:w-auto">
                            {["active", "closed"].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-3 sm:px-4 py-1 sm:py-2 rounded-md md:rounded-xl min-w-[90px] sm:min-w-[120px] flex-1 lg:flex-initial text-xs sm:text-base font-semibold transition border border-[#327691] capitalize ${
                                        activeTab === tab
                                            ? "bg-[#327691] text-white"
                                            : "bg-transparent text-primary dark:text-white"
                                    }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        <div className="flex items-center gap-2 w-full lg:w-1/2">
                            <div className="relative w-full">
                                <CiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl" />
                                <input
                                    type="text"
                                    placeholder="Search jobs"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="rounded-xl pl-10 pr-10 py-2 bg-[#F5F9FE] dark:bg-white placeholder-[#6b7280] outline-none w-full text-[#262626] border border-gray-200 dark:border-none"
                                />
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl"
                                    aria-label="Filter jobs"
                                >
                                    <HiOutlineAdjustmentsHorizontal />
                                </button>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex justify-center pt-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#327691]" />
                        </div>
                    ) : searchedJobs.length === 0 ? (
                        <div className="text-center pt-12">
                            <BsBriefcase className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                            <p className="text-gray-400 text-sm">
                                {searchQuery
                                    ? "No job posts match your search."
                                    : activeTab === "active"
                                        ? "No active job posts yet."
                                        : "No closed job posts."}
                            </p>
                            {activeTab === "active" && !searchQuery && (
                                <button
                                    onClick={() => navigate(ROUTES.JOBS_POST)}
                                    className="mt-4 px-5 py-2.5 bg-[#327691] text-white rounded-xl text-sm font-semibold"
                                >
                                    Post your first job
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {searchedJobs.map((job) => (
                                <div
                                    key={job.id}
                                    className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#242424] cursor-pointer active:opacity-80 transition-opacity"
                                    onClick={() => navigate(`${ROUTES.JOBS_APPLICANTS}/${job.id}`)}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold text-sm truncate">
                                                {job.title}
                                            </h3>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                {job.role} · {job.location}
                                            </p>
                                            <p className="text-xs font-semibold text-[#327691] mt-1">
                                                {job.payRate}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-1">
                                                {applicantCounts[job.id] || 0}{" "}
                                                applicant
                                                {applicantCounts[job.id] !== 1
                                                    ? "s"
                                                    : ""}
                                            </p>
                                        </div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleToggleStatus(job); }}
                                            className={`text-xs px-3 py-1.5 rounded-full font-semibold flex-shrink-0 border transition-all ${
                                                job.status === "active"
                                                    ? "border-red-200 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10"
                                                    : "border-green-200 text-green-500 hover:bg-green-50 dark:hover:bg-green-900/10"
                                            }`}
                                        >
                                            {job.status === "active"
                                                ? "Close"
                                                : "Reopen"}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

const StatCard = ({ label, value, color }) => (
    <div className="bg-gray-50 dark:bg-[#242424] rounded-2xl p-4 text-center border border-gray-100 dark:border-gray-700">
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
    </div>
);

export default MyJobPosts;
